/** Formatação localizada PT-BR (moeda, datas, telefone). */

export type CurrencyCode = 'BRL' | 'EUR' | 'GBP';

/**
 * Números legados: number, decimal com ponto (`3.13`) ou BR (`1.234,56` / `3,13`).
 * Strings inválidas (ex.: erro de `$convert` no aggregate) viram 0.
 */
export function toFiniteNumber(value: unknown): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const raw = String(value).trim();
  if (!raw) return 0;

  if (raw.includes(',')) {
    const normalized = raw.replace(/\./g, '').replace(',', '.');
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

export function currency(value: number | string | null | undefined, code: CurrencyCode = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(
    toFiniteNumber(value),
  );
}

export const brl = (value: number | string | null | undefined): string => currency(value, 'BRL');

/** Prefixo curto para labels (Cashback (€), etc.). */
export function currencySymbol(code: CurrencyCode): string {
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return 'R$';
}

export function compactNumber(value: number | string | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(
    toFiniteNumber(value),
  );
}

export function decimal(value: number | string | null | undefined, fractionDigits = 0): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(toFiniteNumber(value));
}

export function formatDate(value: string | number | Date | null | undefined, fallback = ''): string {
  if (value === null || value === undefined || value === '') return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('pt-BR');
}

export function formatTime(value: string | number | Date | null | undefined, fallback = ''): string {
  if (value === null || value === undefined || value === '') return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function onlyDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

/** Link wa.me a partir de um telefone livre (sanitiza, garante DDI). */
export function whatsappLink(phone: string, assumeBrazil = true, text?: string): string {
  const digits = onlyDigits(phone);
  const number = assumeBrazil && digits.length <= 11 && !digits.startsWith('55') ? `55${digits}` : digits;
  const base = `https://api.whatsapp.com/send?phone=${number}`;
  return text ? `${base}&text=${encodeURIComponent(text)}` : base;
}

export function percent(part: number, total: number, fractionDigits = 1): string {
  if (!total) return '0%';
  return `${((part / total) * 100).toFixed(fractionDigits)}%`;
}

/** Iniciais para avatar (ex.: "Helio Valente" → "HV"). */
export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
